import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { WidgetAction } from '@pickonegift/shared';
import type { WidgetItemPayload } from '@pickonegift/shared';

interface WidgetItem {
    title: string;
    imageUrl: string;
    productUrl?: string;
    sku?: string;
    price?: string;
}

const ALLOWED_ORIGIN = import.meta.env.VITE_WIDGET_ALLOWED_ORIGIN || '*';

export default function WidgetFrame() {
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId') || '';
    const [items, setItems] = useState<WidgetItem[]>([]);
    const [step, setStep] = useState<'items' | 'finalize' | 'done'>('items');
    const [email, setEmail] = useState('');
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [error, setError] = useState('');

    // Listen for postMessage from parent
    const handleMessage = useCallback(
        (event: MessageEvent) => {
            // Origin check (in production, check against allowlist)
            if (ALLOWED_ORIGIN !== '*' && event.origin !== ALLOWED_ORIGIN) return;

            const { type, payload } = event.data || {};

            switch (type) {
                case WidgetAction.ADD_ITEM: {
                    const item = payload as WidgetItemPayload;
                    if (item?.title && item?.imageUrl) {
                        setItems((prev) => {
                            const exists = prev.some((i) => i.title === item.title && i.imageUrl === item.imageUrl);
                            if (exists) return prev;
                            return [...prev, item];
                        });
                    }
                    break;
                }
                case WidgetAction.REMOVE_ITEM: {
                    const idx = payload as number;
                    setItems((prev) => prev.filter((_, i) => i !== idx));
                    break;
                }
                case WidgetAction.GET_ITEMS:
                    sendToParent(WidgetAction.ITEMS_UPDATED, items);
                    break;
            }
        },
        [items],
    );

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        sendToParent(WidgetAction.READY, { companyId });
        return () => window.removeEventListener('message', handleMessage);
    }, [handleMessage, companyId]);

    // Notify parent whenever items change
    useEffect(() => {
        sendToParent(WidgetAction.ITEMS_UPDATED, items);
    }, [items]);

    function sendToParent(type: string, payload?: unknown) {
        if (window.parent !== window) {
            window.parent.postMessage({ type, payload }, ALLOWED_ORIGIN);
        }
    }

    const handleFinalize = async (e: React.FormEvent) => {
        e.preventDefault();
        if (items.length < 2) {
            setError('Dodaj co najmniej 2 prezenty');
            return;
        }
        setLoading(true);
        setError('');

        try {
            // Create session
            const { sessionId } = await api.createWidgetSession({
                companyId,
                items: items.map((i) => ({
                    title: i.title,
                    imageUrl: i.imageUrl,
                    productUrl: i.productUrl,
                    requestSize: false,
                    requestColor: false,
                    requestNote: false,
                })),
            });

            // Finalize
            const result = await api.finalizeWidgetSession(sessionId, {
                creatorEmail: email,
                title: title || 'Lista prezentów',
                backgroundType: 'GENERAL',
            });

            setShareUrl(result.shareUrl);
            setStep('done');
            sendToParent(WidgetAction.FINALIZED, { shareUrl: result.shareUrl });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Błąd');
        } finally {
            setLoading(false);
        }
    };

    const removeItem = (index: number) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div
            style={{
                fontFamily: "'Inter', sans-serif",
                background: '#0f0f23',
                color: '#e8e8f0',
                minHeight: '100vh',
                padding: '16px',
                fontSize: '14px',
            }}
        >
            {/* Header */}
            <div
                style={{
                    textAlign: 'center',
                    padding: '12px',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    marginBottom: '16px',
                }}
            >
                <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>🎁 PickOneGift</span>
            </div>

            {step === 'items' && (
                <>
                    <p style={{ color: '#9090b0', textAlign: 'center', marginBottom: '12px' }}>
                        {items.length === 0
                            ? 'Dodaj produkty ze strony sklepu'
                            : `${items.length} prezent${items.length > 1 ? 'ów' : ''} na liście`}
                    </p>

                    {items.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                            {items.map((item, i) => (
                                <div
                                    key={i}
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                        padding: '10px',
                                        background: 'rgba(255,255,255,0.04)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                    }}
                                >
                                    <img
                                        src={item.imageUrl}
                                        alt=""
                                        style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }}
                                    />
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ fontWeight: 500, fontSize: '0.85rem', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                            {item.title}
                                        </div>
                                        {item.price && (
                                            <div style={{ fontSize: '0.75rem', color: '#7c5cfc' }}>{item.price}</div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => removeItem(i)}
                                        style={{
                                            background: 'rgba(248,113,113,0.15)',
                                            border: '1px solid rgba(248,113,113,0.3)',
                                            color: '#f87171',
                                            borderRadius: '6px',
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {items.length >= 2 && (
                        <button
                            onClick={() => setStep('finalize')}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #7c5cfc 0%, #ff6b9d 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '999px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                            }}
                        >
                            Wygeneruj link 🔗
                        </button>
                    )}
                </>
            )}

            {step === 'finalize' && (
                <form onSubmit={handleFinalize}>
                    <p style={{ color: '#9090b0', marginBottom: '12px' }}>
                        Podaj dane, aby wygenerować link
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Nazwa listy"
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '8px',
                                color: '#e8e8f0',
                                fontSize: '0.9rem',
                                outline: 'none',
                            }}
                        />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Twój email *"
                            required
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '8px',
                                color: '#e8e8f0',
                                fontSize: '0.9rem',
                                outline: 'none',
                            }}
                        />
                    </div>
                    {error && (
                        <p style={{ color: '#f87171', textAlign: 'center', marginTop: '8px', fontSize: '0.85rem' }}>
                            {error}
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                        <button
                            type="button"
                            onClick={() => setStep('items')}
                            style={{
                                flex: 1,
                                padding: '10px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                color: '#9090b0',
                                borderRadius: '999px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                            }}
                        >
                            ← Wróć
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                flex: 2,
                                padding: '10px',
                                background: 'linear-gradient(135deg, #7c5cfc 0%, #ff6b9d 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '999px',
                                fontWeight: 600,
                                cursor: loading ? 'wait' : 'pointer',
                                opacity: loading ? 0.6 : 1,
                                fontSize: '0.85rem',
                            }}
                        >
                            {loading ? '⏳ Tworzę...' : '🎁 Stwórz'}
                        </button>
                    </div>
                </form>
            )}

            {step === 'done' && (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', margin: '20px 0' }}>🎉</div>
                    <h3 style={{ marginBottom: '12px' }}>Lista gotowa!</h3>
                    <p style={{ color: '#9090b0', marginBottom: '16px', fontSize: '0.85rem' }}>
                        Skopiuj link i wyślij go znajomemu
                    </p>
                    <div
                        style={{
                            background: 'rgba(255,255,255,0.05)',
                            padding: '10px',
                            borderRadius: '8px',
                            wordBreak: 'break-all',
                            fontSize: '0.8rem',
                            color: '#7c5cfc',
                            marginBottom: '12px',
                        }}
                    >
                        {shareUrl}
                    </div>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(shareUrl);
                        }}
                        style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #7c5cfc 0%, #ff6b9d 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '999px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                        }}
                    >
                        📋 Kopiuj link
                    </button>
                </div>
            )}
        </div>
    );
}
